import {Box, Button, Stack, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import AddIcon from '@mui/icons-material/Add';
import {useTranslation} from 'react-i18next';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {useAuth} from '../../../../../../contexts/auth/context';
import {ScreenPaths} from '../../../../../../enums';
import {translations} from '../../../../../../i18n/translations';
import {QuickAccessData} from '../../../../../../interfaces/quick-access-data';
import {QuickAccessInfo, QuickAccessManager} from '../../../../../../managers/quick-access-manager';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {DevicesRepository} from '../../../../../../repositories/devices';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import QuickAccessTable from './quick-access-table';
import {UsersRepository} from '../../../../../../repositories/users';
import {UserType} from '../../../../../../backend/enums/user-type';
import {DeviceType} from '../../../../../../backend/enums/device-type';
import QuickAccessAddModal from './quick-access-add-modal';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {Tooltip} from '../../../../../../components/tooltip/tooltip';
import {Device} from '../../../../../../backend/interfaces/device';
import QuickAccessDeactivateModal from './quick-access-deactivate';
import {DeviceWorkgroup} from '../../../../../../backend/enums/device-workgroup';
import {EntitiesRepository} from '../../../../../../repositories/entities';

export default function QuickAccessProfessionalCaregiverSettingsOfficeScreen() {
  const {t} = useTranslation();
  const {state: authState, hasWritePermission} = useAuth();
  const snackBarContext = useSnackBar();
  const [quickAccessData, setQuickAccessData] = useState<QuickAccessData>();
  const [quickAccessEntityName, setQuickAccessEntityName] = useState<string>('');
  const [isActivating, setIsActivating] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<Device>();
  const [isDeactivating, setIsDeactivating] = useState<Device>();
  const {items: professionals, isLoading: isLoadingProfessionals} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      if (!authState.session?.selectedAffiliation?.entity.id) {
        return Promise.resolve({data: [], totalCount: 0});
      }

      return UsersRepository.instance.getInEntity(
        authState.session?.selectedAffiliation?.entity.id,
        limit,
        skip,
        UserType.Caregiver,
        '',
        false
      );
    },
    deps: [authState.session?.selectedAffiliation?.entity.id],
  });
  const {
    items: devices,
    isLoading: isLoadingDevices,
    refreshItems,
  } = useFetchAllItems({
    fetchFunction: async ({limit, skip}) => {
      if (!authState.session?.selectedAffiliation?.entity.id) {
        return Promise.resolve({data: [], totalCount: 0});
      }
      const response = await DevicesRepository.instance.getInEntityAsAffiliatedForEntity(
        authState.session?.selectedAffiliation?.entity.id,
        limit,
        skip,
        true,
        undefined,
        DeviceWorkgroup.QuickAccess
      );

      return response;
    },
    deps: [authState.session?.selectedAffiliation],
  });

  const handleAddDevice = async (title: string, type: DeviceType) => {
    setIsSubmitting(true);

    try {
      const data = await QuickAccessManager.instance.install(
        authState.session?.selectedAffiliation?.entity.id ?? '',
        title,
        type
      );
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(
          translations.office.professional_caregiver.settings.quick_access.activate_modal
            .success_message
        ),
      });
      setQuickAccessData(data);
      refreshItems(true);
      setIsSubmitting(false);
      setIsActivating(false);
    } catch (error) {
      console.warn('🔴 Could not install device.', error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.quick_access.activate_modal
            .error_message
        ),
      });
      setIsSubmitting(false);
      setIsActivating(false);
    }
  };

  const handleEditDevice = async (name: string, type: DeviceType) => {
    setIsSubmitting(true);
    if (isEditing?.id) {
      try {
        await DevicesRepository.instance.updateById(isEditing.id, {
          ...isEditing,
          registeredByUserId: authState.session?.currentUser.id,
          number: name,
          type: type,
        });
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.office.professional_caregiver.settings.quick_access.edit_modal
              .success_message
          ),
        });
        refreshItems(true);
        setIsSubmitting(false);
        setIsEditing(undefined);
      } catch (error) {
        console.warn('🔴 Could not edit device.', error);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.office.professional_caregiver.settings.quick_access.edit_modal
              .error_message
          ),
        });
        setIsSubmitting(false);
        setIsEditing(undefined);
      }
    } else {
      console.warn('🔴 Could not edit device.');
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.quick_access.edit_modal.error_message
        ),
      });
      setIsSubmitting(false);
      setIsEditing(undefined);
    }
  };

  const handleDeactivateDevice = async () => {
    setIsSubmitting(true);
    if (isDeactivating?.id && isDeactivating.entityId) {
      try {
        await QuickAccessManager.instance.uninstall(isDeactivating.entityId, isDeactivating.id);
        if (isDeactivating.id === quickAccessData?.device.id) {
          setQuickAccessData(undefined);
        }

        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.office.professional_caregiver.settings.quick_access.deactivate_modal
              .success_message
          ),
        });
        refreshItems(true);
        setIsSubmitting(false);
        setIsDeactivating(undefined);
      } catch (error) {
        console.warn('🔴 Could not deactivate device.', error);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.office.professional_caregiver.settings.quick_access.deactivate_modal
              .error_message
          ),
        });
        setIsSubmitting(false);
        setIsDeactivating(undefined);
      }
    } else {
      console.warn('🔴 Could not deactivate device.');
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.office.professional_caregiver.settings.quick_access.deactivate_modal
            .error_message
        ),
      });
      setIsSubmitting(false);
      setIsDeactivating(undefined);
    }
  };

  useEffect(() => {
    const getEntity = async (entityId: string) => {
      try {
        const entity = await EntitiesRepository.instance.getById(entityId);
        setQuickAccessEntityName(entity.name);
      } catch (error) {
        console.warn('🔴 Could not get entity.', error);
      }
    };

    const getQuickAccessData = async () => {
      const data: QuickAccessData | null = await QuickAccessManager.instance.data();
      const info: QuickAccessInfo | null = await QuickAccessManager.instance.getInfo();

      if (data && info) {
        // Set the Quick Access Data
        setQuickAccessData(data);

        if (
          data.device.entityId &&
          data.device.entityId !== authState.session?.selectedAffiliation?.entity.id
        ) {
          getEntity(data.device.entityId);
        }
      }
    };

    getQuickAccessData();

    // Check if there is any Quick Access Data
  }, [authState.session?.selectedAffiliation?.entity.id]);

  // Build
  return (
    <Box className="settings-menu-wrapper">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .settings
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}`,
          },
          {
            label: t(
              translations.office.professional_caregiver.settings.generic_header.breadcrumbs
                .quick_access_devices
            ),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${authState.session?.selectedAffiliation?.entity.id}${ScreenPaths.ProfessionalCaregiver_Settings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Occurrences}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="settings-menu-header-title" noWrap>
            {t(
              translations.office.professional_caregiver.settings.generic_header.subtitle
                .quick_access_devices
            )}
          </Typography>
        }
        rightColumn={
          <Tooltip.NotAllowedIf
            condition={
              !!quickAccessData ||
              !hasWritePermission(RolePermissionType.SettingsQuickAccessDevices)
            }
            title={
              !hasWritePermission(RolePermissionType.SettingsQuickAccessDevices)
                ? t(translations.office.professional_caregiver.settings.quick_access.no_permissions)
                : quickAccessEntityName
                  ? t(
                      translations.office.professional_caregiver.settings.quick_access
                        .activate_disabled_ist,
                      {name: quickAccessEntityName}
                    )
                  : t(
                      translations.office.professional_caregiver.settings.quick_access
                        .activate_disabled
                    )
            }
          >
            <Button
              variant="inverted"
              onClick={() => setIsActivating(true)}
              disabled={
                !!quickAccessData ||
                !hasWritePermission(RolePermissionType.SettingsQuickAccessDevices)
              }
              startIcon={<AddIcon />}
            >
              {t(translations.office.professional_caregiver.settings.quick_access.activate_device)}
            </Button>
          </Tooltip.NotAllowedIf>
        }
      />
      <Box>
        {!isLoadingDevices && devices?.length === 0 ? (
          <Stack className="care-table-empty" justifyContent="center" alignItems="center">
            <h3 className="care-table-empty-title">
              {t(translations.office.professional_caregiver.settings.quick_access.empty)}
            </h3>
          </Stack>
        ) : (
          <QuickAccessTable
            canWrite
            isLoading={isLoadingDevices || isLoadingProfessionals}
            data={devices}
            professionals={professionals}
            quickAccessData={quickAccessData}
            onEdit={(deviceId) => {
              const device = devices?.find((device) => device.id === deviceId);
              if (device) setIsEditing(device);
            }}
            onDeactivate={(deviceId) => {
              const device = devices?.find((device) => device.id === deviceId);
              if (device) setIsDeactivating(device);
            }}
          />
        )}
        {isActivating && (
          <QuickAccessAddModal
            onSave={handleAddDevice}
            onClose={() => setIsActivating(false)}
            devices={devices}
            submitting={isSubmitting}
          />
        )}
        {isEditing && (
          <QuickAccessAddModal
            data={{
              name: isEditing.number || '',
              type: isEditing.type,
            }}
            onClose={() => setIsEditing(undefined)}
            onSave={handleEditDevice}
            devices={devices}
            submitting={isSubmitting}
          />
        )}
        {isDeactivating && (
          <QuickAccessDeactivateModal
            submitting={isSubmitting}
            name={isDeactivating.number || ''}
            onClose={() => setIsDeactivating(undefined)}
            onDeactivate={handleDeactivateDevice}
          />
        )}
      </Box>
    </Box>
  );
}
